import {
	Experiments,
	ExperimentsSymbol,
	IPlatformLogger,
	IPropsStore,
	ISessionManager,
	LoggerSymbol,
	METASITE_APP_DEF_ID,
	Props,
} from '@wix/thunderbolt-symbols'
import { withDependencies } from '@wix/thunderbolt-ioc'
import { SessionManagerSymbol } from 'feature-session-manager'
import { ICustomCssAPI, RequestFactoryByFilePath } from './types'
import { createHttpClient } from '@wix/http-client'
import { GLOBAL_CSS_FILE_PATH, GLOBAL_CSS_NAMESPACE, pageId, ViewEnvironment } from './constants'
import { getPageCss, getPageCssDev } from '@wix/ambassador-velo-css-v1-page-css/http'
import { getCssContentDev, getCssContent } from '@wix/ambassador-velo-css-v1-css-content/http'

const getInstance = (getAppInstanceByAppDefId: ISessionManager['getAppInstanceByAppDefId']) => () =>
	getAppInstanceByAppDefId(METASITE_APP_DEF_ID) || ''

export const CustomCssApi = withDependencies(
	[Props, SessionManagerSymbol, LoggerSymbol, ExperimentsSymbol],
	(
		props: IPropsStore,
		sessionManager: ISessionManager,
		logger: IPlatformLogger,
		experiments: Experiments
	): ICustomCssAPI => {
		const getAppInstance = getInstance(sessionManager.getAppInstanceByAppDefId)
		const httpClient = createHttpClient({ getAppToken: getAppInstance, isSSR: !process.env.browser })
		const cssInBlocksNewApiEnabled = experiments['specs.thunderbolt.cssInBlocksNewApi']

		const fetchStylesByFilePath = async (
			gridAppId: string,
			requestFactory: RequestFactoryByFilePath,
			namespace: string = GLOBAL_CSS_NAMESPACE
		) => {
			if (gridAppId) {
				logger.interactionStarted('get_page_css')
				try {
					const request = requestFactory({
						gridAppId,
						filePath: GLOBAL_CSS_FILE_PATH,
						namespace,
					})
					const cssCode = await httpClient
						.request(request)
						.then((response) => response.data.cssContent?.content)
						.catch((error) => {
							if (error?.response?.status === 404) {
								// User possibly deleted CSS file
								return ''
							} else {
								throw new Error(error)
							}
						})
					logger.interactionEnded('get_page_css')
					return `/* Users Custom CSS code */\n ${cssCode}`
				} catch (e) {
					console.error('failed to fetch Custom CSS', e)
				}
			}
		}

		const fetchStylesPerPage = async (
			gridAppId: string,
			viewEnvironment: ViewEnvironment,
			requestFactory: typeof getPageCss
		) => {
			if (gridAppId) {
				logger.interactionStarted('get_page_css')
				try {
					const cssCode = await httpClient
						.request(
							requestFactory({
								gridAppId,
								pageId,
								...(viewEnvironment === ViewEnvironment.PUBLIC ? {} : { viewEnvironment }),
							})
						)
						.then((response) => response.data.pageCss?.content)
						.catch((error) => {
							if (error?.response?.status === 404) {
								// User possibly deleted CSS file
								return ''
							} else {
								throw new Error(error)
							}
						})
					logger.interactionEnded('get_page_css')
					return `/* Users Custom CSS code */\n ${cssCode}`
				} catch (e) {
					console.error('failed to fetch Custom CSS', e)
				}
			}
		}

		const fetchStyles = async (gridAppId: string, viewEnvironment: ViewEnvironment, namespace?: string) => {
			if (viewEnvironment === ViewEnvironment.PUBLIC) {
				if (cssInBlocksNewApiEnabled) {
					return fetchStylesByFilePath(gridAppId, getCssContent, namespace)
				}
				return fetchStylesPerPage(gridAppId, viewEnvironment, getPageCss)
			} else {
				if (cssInBlocksNewApiEnabled) {
					return fetchStylesByFilePath(gridAppId, getCssContentDev, namespace)
				}
				return fetchStylesPerPage(gridAppId, viewEnvironment, getPageCssDev)
			}
		}

		return {
			fetchStyles,
		}
	}
)
